import { Outlet, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Feather, User, Facebook, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: "/", label: "Forside" },
    { href: "/tale", label: "Skriv en tale" },
    { href: "/priser", label: "Priser" },
    { href: "/eksempler", label: "Eksempler" },
    { href: "/blog", label: "Blog" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-header border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-2 group"
              data-testid="logo-link"
            >
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                <Feather className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-heading text-xl md:text-2xl font-bold text-primary">
                MinFesttale
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.href.replace("/", "") || "home"}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-muted"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* CTA + Login - Desktop */}
            <div className="hidden lg:flex items-center gap-2">
              <Link to={localStorage.getItem("mf_token") ? "/mine-taler" : "/login"}>
                <Button 
                  variant="outline"
                  className="rounded-full px-4"
                  data-testid="header-login"
                >
                  <User className="h-4 w-4 mr-2" />
                  {localStorage.getItem("mf_token") ? "Mine taler" : "Log ind"}
                </Button>
              </Link>
              <Link to="/tale">
                <Button 
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full px-6"
                  data-testid="header-cta"
                >
                  Start din tale
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-foreground" />
              ) : (
                <Menu className="h-6 w-6 text-foreground" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border/50 bg-background">
            <nav className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-${link.href.replace("/", "") || "home"}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive(link.href)
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-muted"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-2 space-y-2">
                <Link to={localStorage.getItem("mf_token") ? "/mine-taler" : "/login"} onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    variant="outline"
                    className="w-full rounded-full"
                    data-testid="mobile-login"
                  >
                    <User className="h-4 w-4 mr-2" />
                    {localStorage.getItem("mf_token") ? "Mine taler" : "Log ind"}
                  </Button>
                </Link>
                <Link to="/tale" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full"
                    data-testid="mobile-cta"
                  >
                    Start din tale nu
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <Feather className="w-5 h-5 text-primary" />
                </div>
                <span className="font-heading text-2xl font-bold">MinFesttale</span>
              </div>
              <p className="text-primary-foreground/80 max-w-md">
                Personlige festtaler genereret med AI. 
                Klar på under 2 minutter til en brøkdel af prisen.
              </p>
              {/* Social */}
              <div className="flex items-center gap-3 mt-5">
                <a
                  href="https://facebook.com/minfesttale"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Følg MinFesttale på Facebook"
                  className="w-10 h-10 rounded-full bg-primary-foreground/10 hover:bg-secondary/30 transition-colors flex items-center justify-center"
                  data-testid="footer-facebook"
                >
                  <Facebook className="w-5 h-5 text-primary-foreground" />
                </a>
                <a
                  href="https://instagram.com/minfesttale"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Følg MinFesttale på Instagram"
                  className="w-10 h-10 rounded-full bg-primary-foreground/10 hover:bg-secondary/30 transition-colors flex items-center justify-center"
                  data-testid="footer-instagram"
                >
                  <Instagram className="w-5 h-5 text-primary-foreground" />
                </a>
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-heading text-lg font-bold mb-4">Produkter</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/tale" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors" data-testid="footer-tale">
                    Festtaler
                  </Link>
                </li>
                <li>
                  <Link to="/priser" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors" data-testid="footer-priser">
                    Priser
                  </Link>
                </li>
              </ul>
            </div>

            {/* More Links */}
            <div>
              <h4 className="font-heading text-lg font-bold mb-4">Information</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/eksempler" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors" data-testid="footer-eksempler">
                    Eksempler
                  </Link>
                </li>
                <li>
                  <a href="mailto:hej@minfesttale.dk" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors" data-testid="footer-email">
                    hej@minfesttale.dk
                  </a>
                </li>
                <li>
                  <Link to="/kontakt" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors" data-testid="footer-contact">
                    Kontaktformular
                  </Link>
                </li>
                <li>
                  <Link to="/handelsbetingelser" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors" data-testid="footer-terms">
                    Handelsbetingelser
                  </Link>
                </li>
                <li>
                  <Link to="/privatlivspolitik" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors" data-testid="footer-privacy">
                    Privatlivspolitik
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-primary-foreground/20 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-primary-foreground/60 text-sm">
            <p>&copy; {new Date().getFullYear()} MinFesttale.dk — LearnIQ ApS · CVR 42111007. Alle rettigheder forbeholdes.</p>
            <div className="flex gap-4">
              <Link to="/handelsbetingelser" className="hover:text-primary-foreground transition-colors">
                Handelsbetingelser
              </Link>
              <Link to="/privatlivspolitik" className="hover:text-primary-foreground transition-colors">
                Privatlivspolitik
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
